/**
 * agentFocus.js — what is on screen, as the FocusState the agent and voice resolve "this",
 * "that chart" and "line 3" against (CONTRACT §5.7, §10.2).
 *
 * Pure. Dashboard calls buildFocus whenever the stage, drawer, draft selection, open panel or
 * active case changes; useAgent sends it (throttled) only when focusChanged says so.
 */

/** Overlay panels FocusState knows about; the dossier and the stream are not panels here. */
export const FOCUS_PANELS = new Set(['case_page', 'memory', 'audit', 'rules']);
const MAX_SUSPECTS = 12;

const str = (v) => (typeof v === 'string' && v ? v : null);
const num01 = (v) => (typeof v === 'number' && Number.isFinite(v) ? Math.min(1, Math.max(0, Math.round(v * 100) / 100)) : null);

function drawerFocus(wb) {
  if (!wb || typeof wb !== 'object') return { open: false, tab: null, open_file: null, chart_range: 'all' };
  return {
    open: Boolean(wb.open),
    tab: str(wb.tab),
    open_file: str(wb.path ?? wb.open_file),
    chart_range: str(wb.range ?? wb.chart_range) ?? 'all',
  };
}

function draftFocus(sel) {
  if (!sel || typeof sel !== 'object') return null;
  const id = str(sel.id);
  if (!id) return null;
  return {
    id,
    line_id: str(sel.line_id ?? sel.lineId),
    section_id: str(sel.section_id ?? sel.sectionId),
  };
}

/**
 * @param {object} p
 * @param {string|null} p.activeCaseId
 * @param {string|null} p.stageAsset     asset id on the stage
 * @param {string|null} p.stageSelected  selected part id
 * @param {{name?:string}|null} p.stageNode  the selected node (for part_name)
 * @param {number} p.stageT              explode 0..1
 * @param {string} p.overlay             'none' | 'agent' | 'trace' | …
 * @param {object} p.wb                  reducer wb state (open, tab, path, range)
 * @param {object|null} p.draftSel       {id, lineId|line_id, sectionId|section_id}
 * @param {string|null} p.approvalId     visible ApprovalCard's approval
 * @param {string|null} p.panel          open overlay panel
 * @param {string|null} p.outputId       open output card
 * @param {string[]} p.suspects          ranked suspects on screen
 * @param {{now?: () => Date|number|string}} [opts]
 */
export function buildFocus({
  activeCaseId = null, stageAsset = null, stageSelected = null, stageNode = null, stageT = 0,
  overlay = 'none', wb = null, draftSel = null, approvalId = null, panel = null, outputId = null,
  suspects = null,
} = {}, { now } = {}) {
  const stamp = typeof now === 'function' ? now() : new Date();
  const updated = stamp instanceof Date ? stamp.toISOString() : new Date(stamp).toISOString();
  const partId = str(stageSelected);
  return {
    case_id: str(activeCaseId),
    asset: str(stageAsset),
    part_id: partId,
    part_name: partId ? str(stageNode?.name) : null,
    t: num01(stageT) ?? 0,
    overlay: str(overlay) ?? 'none',
    drawer: drawerFocus(wb),
    draft: draftFocus(draftSel),
    approval_id: str(approvalId),
    panel: FOCUS_PANELS.has(panel) ? panel : null,
    output_id: str(outputId),
    suspects: Array.isArray(suspects) ? suspects.filter((s) => typeof s === 'string' && s).slice(0, MAX_SUSPECTS) : [],
    updated_at: updated,
  };
}

function stableEqual(a, b) {
  if (a === b) return true;
  if (typeof a !== typeof b || a === null || b === null || typeof a !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) return a.length === b.length && a.every((x, i) => stableEqual(x, b[i]));
  const ka = Object.keys(a).filter((k) => k !== 'updated_at');
  const kb = Object.keys(b).filter((k) => k !== 'updated_at');
  if (ka.length !== kb.length) return false;
  return ka.every((k) => Object.prototype.hasOwnProperty.call(b, k) && stableEqual(a[k], b[k]));
}

/** True when two FocusStates differ in anything but updated_at (null/undefined: changed if not both empty). */
export function focusChanged(a, b) {
  if (!a || !b) return Boolean(a) !== Boolean(b);
  return !stableEqual(a, b);
}
